import { useEffect, useState } from 'react';
import {
  DEFAULT_PREFS,
  getPreferences,
  updatePreferences,
  type PreferencesPatch,
  type UserPreferences,
} from '../api/preferences';
import { isDialApiConfigured } from '../api/client';

const MODES = [
  { id: 'user_list', label: 'User-Generated Call List' },
  { id: 'overdue', label: 'Overdue Opportunities' },
  { id: 'campaign', label: 'Campaign Sprint' },
  { id: 'cold_intro', label: 'Cold-ish Intro' },
] as const;

const DIAL_METHODS = [
  { id: '3cx', label: '3CX Click2Call' },
  { id: 'zoho_voice', label: 'Zoho Voice' },
  { id: 'choose', label: 'Ask each time' },
] as const;

const SENSITIVITY = [
  { id: 'low', label: 'Low (gentle)' },
  { id: 'medium', label: 'Medium' },
  { id: 'high', label: 'High (sharp)' },
] as const;

export function PreferencesPage() {
  const [prefs, setPrefs] = useState<UserPreferences>(DEFAULT_PREFS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getPreferences()
      .then((p) => {
        if (active) setPrefs(p);
      })
      .catch((err: unknown) => {
        if (active) setError(err instanceof Error ? err.message : 'load failed');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const save = async (patch: PreferencesPatch) => {
    setSaving(true);
    setError(null);
    try {
      const next = await updatePreferences(patch);
      setPrefs(next);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="px-8 py-10">
      <div className="mx-auto max-w-xl">
        <h1 className="font-sans text-3xl font-semibold tracking-tight text-graphite-900">
          Preferences
        </h1>
        <p className="mt-2 font-sans text-sm leading-relaxed text-graphite-700">
          Defaults applied at sprint start.
        </p>

        {!isDialApiConfigured ? (
          <p className="mt-4 rounded-md border border-dashed border-graphite-300 bg-surface-sunk px-4 py-2 font-mono text-xs text-graphite-500">
            Worker not reachable — preview build, changes won’t persist.
          </p>
        ) : null}

        {loading ? (
          <p className="mt-8 font-mono text-xs text-graphite-500">loading…</p>
        ) : (
          <div className="mt-8 space-y-8">
            <Field label="Daily points target">
              <NumberPair
                value={prefs.daily_points_target}
                unit="pts"
                min={1}
                max={500}
                onCommit={(v) => save({ daily_points_target: v })}
              />
            </Field>

            <Field label="Default mode on app open">
              <ChipSelect
                options={MODES}
                value={prefs.default_mode}
                onSelect={(v) => save({ default_mode: v })}
              />
            </Field>

            <Field label="Preferred dial method">
              <ChipSelect
                options={DIAL_METHODS}
                value={prefs.preferred_dial_method}
                onSelect={(v) => save({ preferred_dial_method: v })}
              />
            </Field>

            <Field label="Idle nudge sensitivity">
              <ChipSelect
                options={SENSITIVITY}
                value={prefs.idle_nudge_sensitivity}
                onSelect={(v) => save({ idle_nudge_sensitivity: v })}
              />
            </Field>
          </div>
        )}

        <p className="mt-10 font-mono text-[0.7rem] text-graphite-500">
          {saving ? 'saving…' : prefs.updated_at ? `last saved ${prefs.updated_at}` : ''}
          {error ? <span className="ml-2 text-signal-amber">· {error}</span> : null}
        </p>
      </div>
    </main>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-graphite-500">
        {label}
      </h3>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function NumberPair({
  value,
  unit,
  min,
  max,
  onCommit,
}: {
  value: number;
  unit: string;
  min: number;
  max: number;
  onCommit: (v: number) => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <div className="flex w-32 items-baseline gap-2 rounded-md border border-graphite-300 bg-surface px-3 py-2">
      <input
        type="number"
        min={min}
        max={max}
        value={draft}
        onChange={(e) => setDraft(parseInt(e.target.value, 10) || min)}
        onBlur={() => {
          if (draft !== value) onCommit(draft);
        }}
        className="w-full bg-transparent font-sans text-xl font-semibold text-graphite-900 outline-none"
      />
      <span className="font-mono text-xs text-graphite-500">{unit}</span>
    </div>
  );
}

function ChipSelect<T extends string>({
  options,
  value,
  onSelect,
}: {
  options: readonly { id: T; label: string }[];
  value: T;
  onSelect: (v: T) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const active = opt.id === value;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onSelect(opt.id)}
            className={
              'rounded-full border px-3 py-1.5 font-sans text-xs transition-colors ' +
              (active
                ? 'border-signal-amber bg-signal-amber/10 text-signal-amber'
                : 'border-graphite-300 bg-surface text-graphite-700 hover:bg-surface-sunk')
            }
            aria-pressed={active}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
